import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, Check, MapPin, Calendar as Cal, Clock, CalendarPlus, UserPlus } from "lucide-react";
import api, { API } from "@/lib/api";
import { useClientAuth } from "@/context/ClientAuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const bookingId = params.get("booking_id");
  const [status, setStatus] = useState("checking"); // checking | confirmed | error
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const { data: b } = await api.get(`/bookings/confirmation/${bookingId}`);
          setBooking(b);
          setStatus("confirmed");
          return;
        }
      } catch {
        /* keep trying */
      }
      if (attempts >= 10) {
        setStatus("error");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    if (sessionId && bookingId) poll();
    else setStatus("error");
    return () => clearTimeout(timer);
  }, [sessionId, bookingId]);

  return (
    <div className="min-h-screen bg-[#FAF8F5] text-[#1C1917] flex items-center justify-center px-5 py-16">
      <div className="max-w-md w-full">
        {status === "checking" && (
          <div data-testid="payment-checking" className="text-center">
            <Loader2 size={40} className="animate-spin text-[#C5B382] mx-auto" />
            <p className="mt-6 text-[#1C1917]/60">Confirming your payment…</p>
          </div>
        )}

        {status === "confirmed" && booking && (
          <div data-testid="payment-confirmed" className="border border-[#1C1917]/10 bg-white">
            <div className="p-8 text-center border-b border-[#1C1917]/10">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-[#C5B382] mb-5">
                <Check size={28} className="text-[#C5B382]" />
              </div>
              <h1 className="font-serif text-3xl">Appointment Confirmed ✨</h1>
              <p className="text-[#1C1917]/60 mt-3 text-sm">
                Thank you, {booking.client_name}. A confirmation email is on its way.
              </p>
            </div>
            <div className="p-8 space-y-4 text-sm">
              <Row label="Service" value={booking.service_name} />
              <Row label="Date" value={booking.date_display} icon={Cal} />
              <Row label="Time" value={booking.time} icon={Clock} />
              <Row label="Amount paid today" value={`$${booking.amount_paid.toFixed(2)} AUD`} />
              <Row
                label="Balance at appointment"
                value={booking.remaining > 0 ? `$${booking.remaining.toFixed(2)} AUD` : "Fully paid"}
              />
              <div className="border-t border-[#1C1917]/10 pt-4">
                <div className="flex items-center gap-2 text-[#C5B382] eyebrow mb-2">
                  <MapPin size={14} /> Studio Location
                </div>
                <p className="text-[#1C1917]/70 leading-relaxed">{booking.studio_address}</p>
              </div>
            </div>
            <div className="p-6 border-t border-[#1C1917]/10 space-y-3">
              <CreateAccountCard booking={booking} />
              <a
                href={`${API}/bookings/${bookingId}/calendar.ics`}
                data-testid="add-to-calendar"
                className="flex items-center justify-center gap-2 bg-[#C5B382] hover:bg-white text-[#0A0A0A] text-[12px] font-semibold tracking-[0.18em] uppercase py-3.5 transition-colors"
              >
                <CalendarPlus size={16} /> Add to Calendar
              </a>
              <a
                href="https://instagram.com/slavic.lashes.brows"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-ig"
                className="block text-center border border-[#1C1917]/15 hover:border-[#C5B382] text-[12px] font-semibold tracking-[0.18em] uppercase py-3.5 transition-colors"
              >
                Can't make it? Message @slavic.lashes.brows
              </a>
              <Link
                to="/"
                data-testid="back-home"
                className="block text-center text-[#1C1917]/45 hover:text-[#1C1917] text-[11px] tracking-[0.18em] uppercase py-2 transition-colors"
              >
                Back to Home
              </Link>
            </div>
          </div>
        )}

        {status === "error" && (
          <div data-testid="payment-error" className="text-center border border-[#1C1917]/10 bg-white p-8">
            <h1 className="font-serif text-2xl">We couldn't confirm your payment</h1>
            <p className="text-[#1C1917]/60 mt-3 text-sm">
              If you were charged, your booking is safe — please check your email or contact us.
            </p>
            <Link
              to="/"
              className="inline-block mt-6 border border-[#1C1917]/15 hover:border-[#C5B382] text-[12px] font-semibold tracking-[0.18em] uppercase px-8 py-3.5 transition-colors"
            >
              Back to Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, icon: Icon }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-[#1C1917]/45 flex items-center gap-2">
        {Icon && <Icon size={14} className="text-[#C5B382]" />}
        {label}
      </span>
      <span className="text-[#1C1917] font-medium text-right">{value}</span>
    </div>
  );
}

function CreateAccountCard({ booking }) {
  const { client, setClient } = useClientAuth();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  // Only offer to guests (client === false means checked & signed out).
  if (client) return null;

  if (done) {
    return (
      <div data-testid="account-created" className="border border-[#C5B382] bg-[#F8F4EA] rounded-2xl p-5 text-center">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#C5B382] mb-3">
          <Check size={18} className="text-[#C5B382]" />
        </div>
        <p className="text-sm text-[#1C1917] font-medium">Account created 🎉</p>
        <p className="text-xs text-[#1C1917]/55 mt-1">You can now view and manage all your bookings anytime.</p>
        <Link
          to="/account"
          data-testid="go-to-account"
          className="inline-block mt-4 bg-[#C5B382] hover:bg-[#B8A572] text-[#1C1917] text-[11px] font-semibold tracking-[0.18em] uppercase px-6 py-3 rounded-full transition-colors"
        >
          Go to My Account
        </Link>
      </div>
    );
  }

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/client-auth/register", {
        name: booking.client_name,
        email: booking.client_email,
        phone: booking.client_phone || "",
        password,
      });
      setClient(data);
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not create your account.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="create-account-card" className="border border-[#1C1917]/12 rounded-2xl p-5 bg-[#FAF8F5]">
      <div className="flex items-center gap-2 text-[#C5B382] eyebrow mb-2">
        <UserPlus size={15} /> Manage your bookings
      </div>
      <p className="text-sm text-[#1C1917]/70 leading-relaxed mb-3">
        Create an account for <span className="font-medium text-[#1C1917]">{booking.client_email}</span> to reschedule, cancel and rebook anytime.
      </p>
      <input
        data-testid="account-password"
        type="password"
        value={password}
        onChange={(e) => { setPassword(e.target.value); setErr(""); }}
        placeholder="Choose a password (min 6 characters)"
        className="w-full border border-[#1C1917]/15 rounded-xl px-3 py-2.5 text-sm text-[#1C1917] bg-white focus:border-[#C5B382] outline-none"
      />
      {err && <p data-testid="account-error" className="text-red-500 text-xs mt-2">{err}</p>}
      <button
        data-testid="create-account-btn"
        onClick={submit}
        disabled={busy || password.trim().length < 6}
        className="w-full mt-3 flex items-center justify-center gap-2 bg-[#1A1918] hover:bg-[#000] disabled:opacity-40 disabled:cursor-not-allowed text-white text-[11px] font-semibold tracking-[0.18em] uppercase py-3 rounded-full transition-colors"
      >
        {busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Create account
      </button>
    </div>
  );
}
